import type { BootContext, BootStrategy } from "./types";
import {
  registerAllLayerAResources,
  finalizeAllLayerAResources,
  getLayerBStores,
  purgeLegacyReceiptOrderCache,
} from "./registry";
import { serveAllVersionsFromCache } from "~/services/repos/versionSync";
import { useAuthStore } from "~/stores/auth";

/**
 * Full offline boot. Never touches Firestore or RTDB for initial data —
 * everything comes from IDB / localStorage.
 *
 *  1. Auth: restore from localStorage snapshot. If absent → throw `need
 *     login`, bootstrap redirects to /login.
 *  2. Layer A: register resources, then apply whatever IDB envelopes exist
 *     (skip Firestore probe entirely). Finalize stays the same — non-
 *     version-tracked auxiliaries are cache-first by design.
 *  3. Layer B: each store restores from IDB into Pinia immediately and
 *     subscribes RTDB in the background (writes queue locally; pick up
 *     when connectivity returns).
 */
export class OfflineBootStrategy implements BootStrategy {
  readonly mode = "offline" as const;

  async loadAuth(): Promise<void> {
    await useAuthStore().hydrateFromLocalStorage();
  }

  async loadLayerA(_ctx: BootContext): Promise<void> {
    registerAllLayerAResources();
    await serveAllVersionsFromCache();
    await finalizeAllLayerAResources();
  }

  async bootLayerB(ctx: BootContext): Promise<void> {
    purgeLegacyReceiptOrderCache();
    await Promise.all(getLayerBStores().map((s) => s.bootOffline(ctx)));
  }
}
